import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Navigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading, error, profileReady, role } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();
  const location = useLocation();

  if (authLoading || (!profileReady && user)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center space-y-3">
          <h1 className="text-lg font-semibold text-foreground">Unable to load dashboard data</h1>
          <p className="text-sm text-muted-foreground">Please refresh.</p>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Refresh
            </Button>
            <Button onClick={() => window.location.assign("/login")}>Go to Login</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Super Admin manages platform and all companies; bypass company onboarding requirement
  if (role === "super_admin") {
    return <>{children}</>;
  }

  // While company records load from MySQL, show spinner to avoid false redirection
  if (companyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary" />
      </div>
    );
  }

  const isOnboardingRoute = location.pathname === "/dashboard/onboarding";
  const needsOnboarding = !activeCompany || !activeCompany.onboardingCompleted;

  if (needsOnboarding && !isOnboardingRoute) {
    return <Navigate to="/dashboard/onboarding" replace />;
  }

  if (!needsOnboarding && isOnboardingRoute) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
